import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import { CheckCircle, Repeat, Download, Trash, DollarSign, Bell, ShieldCheck, MapPin, PauseCircle, PlayCircle, ChevronDown } from "lucide-react";
import "./AcceptedVendors.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const EXTRA_PRICES = {
  "Hay Bales":     500,
  "Wristbands":    300,
  "Single Socket": 2900,
};

const PAYMENT_FILTERS = [
  { value: "all",     label: "All vendors"         },
  { value: "paid",    label: "Fully paid"          },
  { value: "balance", label: "Balance submitted"   },
  { value: "deposit", label: "Deposit submitted"   },
  { value: "unpaid",  label: "Unpaid"              },
  { value: "on_hold", label: "On hold"             },
];

const AVATAR_PALETTES = [
  { bg: "#e0e7ff", color: "#4338ca" },
  { bg: "#d1fae5", color: "#065f46" },
  { bg: "#fef3c7", color: "#92400e" },
  { bg: "#fee2e2", color: "#991b1b" },
  { bg: "#ede9fe", color: "#5b21b6" },
  { bg: "#e0f2fe", color: "#0369a1" },
  { bg: "#fce7f3", color: "#9d174d" },
];

function avatarPalette(name = "") {
  const idx = name.charCodeAt(0) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[idx];
}

function VendorAvatar({ logoUrl, brandName, slug }) {
  const [errored, setErrored] = useState(false);
  const initials = (brandName || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();
  const palette  = avatarPalette(brandName);

  const content = logoUrl && !errored ? (
    <div className="avp-avatar">
      <img src={logoUrl} alt={brandName} onError={() => setErrored(true)} />
    </div>
  ) : (
    <div className="avp-avatar" style={{ background: palette.bg, color: palette.color }}>
      {initials}
    </div>
  );

  if (slug) {
    return (
      <a
        href={`/v/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
        title="View vendor profile"
      >
        {content}
      </a>
    );
  }

  return content;
}

export default function AcceptedVendors() {
  const [events, setEvents]               = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [applications, setApplications]   = useState([]);
  const [slots, setSlots]                 = useState({});
  const [loading, setLoading]             = useState(false);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [sending, setSending]             = useState({});
  const [query, setQuery]                 = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [expanded, setExpanded]           = useState({});

  useEffect(() => {
    const fetchEvents = async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, start_date")
        .order("start_date", { ascending: false });

      if (error) { 
        console.error("[AV] fetchEvents:", error); 
        setEventsLoading(false); 
        return; 
      }
      setEvents(data || []);

      const targetEvent = 
        data?.find(ev => ev.name?.toLowerCase().includes("bizarre bazaar")) ||
        data?.find(ev => ev.name?.toLowerCase().includes("christmas")) ||
        data?.[0];

      setSelectedEvent(targetEvent?.id || null);
      setEventsLoading(false);
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!selectedEvent) return;
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);

      // Added: reactivated_at, deposit_deadline
      const { data: appsData, error: appsError } = await supabase
        .from("vendor_applications")
        .select(`
          id, vendor_id, event_id, status, tent_type, description, whatsapp_number, location,
          created_at, extras, invoice_total, deposit_required,
          deposit_submitted, deposit_proof_url, deposit_confirmed,
          balance_submitted, balance_proof_url, balance_confirmed,
          reminder_sent, confirmation_sent, onboarding_completed, chosen_spot,
          accepted_at, reactivated_at, deposit_deadline,
          previous_chosen_spot, previous_sub_id, previous_tent_mode,
          vendors (
            id, brand_name, contact_person, email,
            phone_number, website, instagram, facebook,
            logo_url, logo_path, slug
          )
        `)
        .eq("event_id", selectedEvent)
        .in("status", ["approved", "on_hold"])
        .order("created_at", { ascending: true });

      if (appsError) { 
        console.error("[AV] apps:", appsError); 
        setLoading(false); 
        return; 
      }
      if (!mounted) return;

      const appIds = appsData.map(a => a.id);
      if (appIds.length === 0) { 
        setApplications([]); 
        setSlots({}); 
        setLoading(false); 
        return; 
      }

      const { data: slotsData, error: slotsError } = await supabase
        .from("vendor_slots")
        .select("vendor_application_id, slot_id, sub_id, assigned_at, tent_mode")
        .in("vendor_application_id", appIds);

      if (slotsError) console.error("[AV] slots:", slotsError);

      const slotMap = {};
      slotsData?.forEach(s => { slotMap[s.vendor_application_id] = s; });

      if (!mounted) return;
      setApplications(appsData || []);
      setSlots(slotMap);
      setLoading(false);
    };

    fetchAll();
    return () => { mounted = false; };
  }, [selectedEvent]);

  const filtered = useMemo(() => {
    let r = applications;

    if (paymentFilter === "on_hold") {
      r = r.filter(a => a.status === "on_hold");
    } else {
      r = r.filter(a => a.status !== "on_hold");
      if (paymentFilter === "paid")         r = r.filter(a => a.balance_confirmed);
      else if (paymentFilter === "balance") r = r.filter(a => a.balance_submitted && !a.balance_confirmed);
      else if (paymentFilter === "deposit") r = r.filter(a => a.deposit_submitted && !a.balance_submitted && !a.balance_confirmed);
      else if (paymentFilter === "unpaid")  r = r.filter(a => !a.deposit_submitted);
    }

    const q = query.trim().toLowerCase();
    if (q) {
      r = r.filter(a => [a.vendors?.brand_name, a.vendors?.email, a.tent_type].join(" ").toLowerCase().includes(q));
    }

    r = [...r].sort((a, b) =>
      (a.vendors?.brand_name || "").localeCompare(b.vendors?.brand_name || "", undefined, { sensitivity: "base" })
    );

    return r;
  }, [applications, query, paymentFilter]);

  const counts = useMemo(() => {
    const active = applications.filter(a => a.status !== "on_hold");
    return {
      all:     active.length,
      paid:    active.filter(a => a.balance_confirmed).length,
      balance: active.filter(a => a.balance_submitted && !a.balance_confirmed).length,
      deposit: active.filter(a => a.deposit_submitted && !a.balance_submitted && !a.balance_confirmed).length,
      unpaid:  active.filter(a => !a.deposit_submitted).length,
      on_hold: applications.filter(a => a.status === "on_hold").length,
    };
  }, [applications]);

  const BALANCE_DEADLINE = new Date("2026-11-14T23:59:59+03:00");

  // 48h for first acceptance, 24h for reactivation, prioritizing explicit DB deposit_deadline
  const getDepositDeadline = (app) => {
    if (app.deposit_deadline) {
      return new Date(app.deposit_deadline);
    }
    if (app.reactivated_at) {
      return new Date(new Date(app.reactivated_at).getTime() + 24 * 60 * 60 * 1000);
    }
    if (app.accepted_at) {
      return new Date(new Date(app.accepted_at).getTime() + 48 * 60 * 60 * 1000);
    }
    return null;
  };

  const getCountdown = (deadline) => {
    if (!deadline || isNaN(deadline.getTime())) return null;
    const msLeft = deadline.getTime() - Date.now();
    if (msLeft <= 0) return { label: "overdue", urgency: "urgent" };
    const totalHours = msLeft / 3600000;
    const days       = Math.floor(msLeft / 86400000);
    const weeks      = Math.floor(days / 7);
    if (weeks >= 2)       return { label: `${weeks}w left`,   urgency: "ok"     };
    if (weeks === 1)      return { label: `${days}d left`,    urgency: "ok"     };
    if (days >= 3)        return { label: `${days}d left`,    urgency: "soon"   };
    if (totalHours >= 24) return { label: `${days}d left`,    urgency: "urgent" };
    const hrs = Math.floor(totalHours);
    return               { label: hrs < 1 ? "< 1h left" : `${hrs}h left`, urgency: "urgent" };
  };

  const updateApp = (id, patch) =>
    setApplications(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a));

  const holdVendor = async (app) => {
    if (!window.confirm(`Put ${app.vendors?.brand_name} on hold?\n\nThis will release their slot and suspend their account.`)) return;

    try {
      setSending(s => ({ ...s, [app.id + "_hold"]: true }));

      const currentSlot = slots[app.id];
      const spotToStash = app.chosen_spot || currentSlot?.slot_id || null;
      const subToStash  = currentSlot?.sub_id || "A";
      const modeToStash = currentSlot?.tent_mode || (app.tent_type === "20x20_shared" ? "20x20-double" : "20x20-single");

      // 1. Delete physical slot assignment
      const { error: slotDelError } = await supabase
        .from("vendor_slots")
        .delete()
        .eq("vendor_application_id", app.id);

      if (slotDelError) {
        console.error("[holdVendor] Error deleting slot:", slotDelError);
      }

      // 2. Put application on hold and stash previous spot fields
      const updatePayload = {
        status: "on_hold",
        chosen_spot: null,
        previous_chosen_spot: spotToStash,
        previous_sub_id: subToStash,
        previous_tent_mode: modeToStash,
        deposit_reminder_48h_sent: false,
        deposit_reminder_24h_sent: false,
        deposit_reminder_12h_sent: false,
        deposit_reminder_final_sent: false,
      };

      const { error: appErr } = await supabase
        .from("vendor_applications")
        .update(updatePayload)
        .eq("id", app.id);

      if (appErr) throw appErr;

      // 3. Update local state
      setSlots(prev => {
        const next = { ...prev };
        delete next[app.id];
        return next;
      });

      updateApp(app.id, updatePayload);

      // 4. Send balance/deposit expired email
      try {
        const eventName = events.find(ev => ev.id === selectedEvent)?.name;
        const res = await fetch("/api/send-balance-expired-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: app.vendors?.email,
            cc: ["info@bizbazevents.com"],
            brandName: app.vendors?.brand_name,
            eventName,
          }),
        });
        const data = await res.json();
        if (!res.ok) console.error("[holdVendor] email failed:", data);
      } catch (emailErr) {
        console.error("[holdVendor] email fetch error:", emailErr);
      }
    } catch (err) {
      alert("Failed to hold vendor: " + err.message);
    } finally {
      setSending(s => ({ ...s, [app.id + "_hold"]: false }));
    }
  };

  const activateVendor = async (app) => {
    if (!window.confirm(`Reactivate ${app.vendors?.brand_name}? They will be given a 24-hour deposit deadline.`)) return;

    try {
      setSending(s => ({ ...s, [app.id + "_activate"]: true }));

      const now = new Date();
      const reactivatedAt = now.toISOString();
      const newDeadline = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(); // 24 hours from reactivation

      const updatePayload = {
        status: "approved",
        reactivated_at: reactivatedAt,
        deposit_deadline: newDeadline,
        deposit_reminder_48h_sent: false,
        deposit_reminder_24h_sent: false,
        deposit_reminder_12h_sent: false,
        deposit_reminder_final_sent: false,
      };

      const { error } = await supabase
        .from("vendor_applications")
        .update(updatePayload)
        .eq("id", app.id);

      if (error) throw error;

      updateApp(app.id, updatePayload);

      try {
        const res = await fetch("/api/send-email", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to:      app.vendors?.email,
            cc:      ["info@bizbazevents.com"],
            subject: "Your Biz Baz vendor account for Bizarre Bazaar Christmas Festival has been reactivated",
            html: `
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0;padding:0;background-color:#f4f5f7;font-family:Arial,Helvetica,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f5f7;padding:40px 16px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:12px;overflow:hidden;border:1px solid #e8eaed;">
        <tr><td style="height:4px;background-color:#16a34a;font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr><td align="center" style="padding:32px 40px 24px;">
          <p style="margin:0;font-size:18px;font-weight:bold;color:#111827;">Biz Baz Events</p>
          <p style="margin:4px 0 0;font-size:12px;color:#6b7280;">Christmas Festival 2026</p>
        </td></tr>
        <tr><td style="padding:0 40px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="height:1px;background-color:#e8eaed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>
        <tr><td style="padding:32px 40px;">
          <p style="margin:0 0 16px;font-size:16px;color:#111827;">Hi <strong>${app.vendors?.brand_name || "there"}</strong>,</p>
          <p style="margin:0 0 16px;font-size:15px;color:#374151;line-height:1.6;">
            Your vendor account for <strong style="color:#111827;">Bizarre Bazaar Christmas Festival 2026</strong> has been reactivated.
          </p>
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:24px;background-color:#fef2f2;border-left:3px solid #dc2626;border-radius:0 6px 6px 0;">
            <tr><td style="padding:14px 16px;">
              <p style="margin:0 0 4px;font-size:13px;font-weight:bold;color:#991b1b;">Action required within 24 hours</p>
              <p style="margin:0;font-size:13px;color:#991b1b;line-height:1.6;">
                Upload proof of payment of the 50% non-refundable deposit to secure your space. If you fail to do so, your vendor account for the festival will be deactivated.
              </p>
            </td></tr>
          </table>
          <p style="margin:0 0 24px;font-size:15px;color:#374151;line-height:1.6;">
            Log in to your vendor portal to upload your deposit payment proof now.
          </p>
          <table cellpadding="0" cellspacing="0" border="0" style="margin-bottom:32px;">
            <tr>
              <td align="center" style="background-color:#111827;border-radius:8px;">
                <a href="https://bizbazevents.com/vendor/login" target="_blank"
                   style="display:inline-block;padding:13px 28px;font-size:14px;font-weight:bold;color:#ffffff;text-decoration:none;">
                  Log in to Vendor Portal &rarr;
                </a>
              </td>
            </tr>
          </table>
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:28px;">
            <tr>
              <td>
                <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f9fafb;border:1px solid #e8eaed;border-radius:8px;">
                  <tr><td style="padding:12px 16px;">
                    <p style="margin:0 0 2px;font-size:10px;font-weight:bold;letter-spacing:0.06em;text-transform:uppercase;color:#9ca3af;">Deposit payment due</p>
                    <p style="margin:0;font-size:13px;font-weight:bold;color:#dc2626;">Within 24 hours</p>
                  </td></tr>
                </table>
              </td>
            </tr>
          </table>
          <p style="margin:0 0 4px;font-size:14px;color:#6b7280;">Reply to this email if you have any questions.</p>
          <p style="margin:0 0 28px;font-size:14px;color:#6b7280;">— The Biz Baz Events Team</p>
          <p style="margin:0;font-size:11px;color:#9ca3af;line-height:1.6;border-top:1px solid #e8eaed;padding-top:16px;">
            Please note: direct payments made without completing the onboarding process in full will not be accepted and will be refunded. Your spot is only confirmed once full payment has been uploaded and confirmed.
          </p>
        </td></tr>
        <tr><td style="padding:0 40px;"><table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="height:1px;background-color:#e8eaed;font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>
        <tr><td align="center" style="padding:20px 40px 28px;">
          <p style="margin:0;font-size:11px;color:#9ca3af;">
            &copy; 2026 Biz Baz Events &nbsp;&middot;&nbsp;
            <a href="https://bizbazevents.com/privacy-policy" style="color:#9ca3af;text-decoration:underline;">Privacy Policy</a>
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`,
          }),
        });
        const data = await res.json();
        if (!res.ok) console.error("[activateVendor] email failed:", data);
      } catch (emailErr) {
        console.error("[activateVendor] email fetch error:", emailErr);
      }
    } catch (err) {
      alert("Failed to activate vendor: " + err.message);
    } finally {
      setSending(s => ({ ...s, [app.id + "_activate"]: false }));
    }
  };

  const exportPaidVendorsToExcel = () => {
    const paidVendors = applications.filter(
      a => a.deposit_confirmed || a.balance_confirmed
    );

    if (!paidVendors.length) {
      alert("No paid vendors to export.");
      return;
    }

    const rows = paidVendors.map(a => {
      const slot = slots[a.id];

      return {
        "Brand Name": a.vendors?.brand_name || "",
        "Contact Person": a.vendors?.contact_person || "",
        "Email": a.vendors?.email || "",
        "Phone": a.vendors?.phone_number || "",
        "WhatsApp": a.whatsapp_number || "",
        "Tent Type": a.tent_type || "",
        "Slot": slot?.slot_id || "",
        "Location": a.location || "",
        "Deposit Submitted": a.deposit_submitted ? "Yes" : "No",
        "Deposit Confirmed": a.deposit_confirmed ? "Yes" : "No",
        "Balance Submitted": a.balance_submitted ? "Yes" : "No",
        "Balance Confirmed": a.balance_confirmed ? "Yes" : "No",
        "Invoice Total": a.invoice_total || 0,
        "Deposit Required": a.deposit_required || 0,
        "Status": a.balance_confirmed
          ? "Fully Paid"
          : a.deposit_submitted
          ? "Deposit Paid"
          : "Pending",
        "Hay Bales": Number(a.extras?.["Hay Bales"] || 0),
        "Single Socket": Number(a.extras?.["Single Socket"] || 0),
        "Wristbands": Number(a.extras?.["Wristbands"] || 0),
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 30 }, { wch: 25 }, { wch: 30 }, { wch: 18 }, { wch: 18 },
      { wch: 20 }, { wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 18 },
      { wch: 18 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 15 }, { wch: 12 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Paid Vendors");
    XLSX.writeFile(wb, `paid_vendors_${selectedEvent}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const confirmDeposit = async (app) => {
    if (!window.confirm(`Confirm deposit for ${app.vendors?.brand_name}?`)) return;
    try {
      setSending(s => ({ ...s, [app.id + "_d"]: true }));
      const { error } = await supabase.from("vendor_applications").update({ 
        deposit_confirmed: true 
      }).eq("id", app.id);
      if (error) throw error;
      const res = await fetch("/api/send-deposit-confirmation.js", { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify({ applicationId: app.id }) 
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Email failed");
      updateApp(app.id, { deposit_confirmed: true });
    } catch (err) { 
      alert("Failed: " + err.message); 
    } finally { 
      setSending(s => ({ ...s, [app.id + "_d"]: false })); 
    }
  };

  const confirmFullPayment = async (app) => {
    if (!window.confirm(`Confirm full payment for ${app.vendors?.brand_name}?`)) return;
    try {
      setSending(s => ({ ...s, [app.id + "_f"]: true }));
      const { error } = await supabase.from("vendor_applications").update({
        deposit_submitted: true, deposit_confirmed: true,
        balance_submitted: true, balance_confirmed: true,
      }).eq("id", app.id);
      if (error) throw error;
      const res = await fetch("/api/send-full-payment-email", { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify({ applicationId: app.id }) 
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Email failed");
      updateApp(app.id, { deposit_submitted: true, deposit_confirmed: true, balance_submitted: true, balance_confirmed: true });
    } catch (err) { 
      alert("Failed: " + err.message); 
    } finally { 
      setSending(s => ({ ...s, [app.id + "_f"]: false })); 
    }
  };

  const downloadDocuments = async (app) => {
    try {
      const safeBrand = (app.vendors?.brand_name || "").replace(/\s+/g, "_").toLowerCase();
      if (!safeBrand) return alert("No vendor found.");
      const { data: files, error } = await supabase.storage.from("Summer_Vendors_2026").list(safeBrand);
      if (error) throw error;
      if (!files?.length) return alert("No files found.");
      const zip = new JSZip();
      await Promise.all(files.map(async f => {
        const { data, error: dlErr } = await supabase.storage.from("Summer_Vendors_2026").download(`${safeBrand}/${f.name}`);
        if (!dlErr) zip.file(f.name, data);
      }));
      saveAs(await zip.generateAsync({ type: "blob" }), `${safeBrand}_documents.zip`);
    } catch (err) { 
      alert("Failed: " + err.message); 
    }
  };

  const exportExtrasToExcel = () => {
    const eligible = applications.filter(a => a.deposit_confirmed || a.balance_confirmed);
    if (!eligible.length) { 
      alert("No vendors with confirmed deposits to export."); 
      return; 
    }
    const rows = eligible.map(a => ({
      "Brand Name":    a.vendors?.brand_name,
      "Hay Bales":     Number(a.extras?.["Hay Bales"]     || 0),
      "Single Socket": Number(a.extras?.["Single Socket"] || 0),
      "Wristbands":    Number(a.extras?.["Wristbands"]    || 0),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 16 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Extras");
    XLSX.writeFile(wb, `extras_${selectedEvent}.xlsx`);
  };

  const toggleExpand = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  if (eventsLoading) return (
    <div className="avp-page">
      <div className="avp-loading"><div className="avp-spinner" /><span>Loading…</span></div>
    </div>
  );

  const eligibleForExport = applications.filter(a => a.deposit_confirmed || a.balance_confirmed);

  return (
    <div className="avp-page">

      {/* ── Header ── */}
      <div className="avp-header">
        <div>
          <h1 className="avp-title">Accepted vendors</h1>
          <p className="avp-sub">{filtered.length} of {applications.length} vendor{applications.length !== 1 ? "s" : ""} · sorted A–Z</p>
        </div>
        <div className="avp-header-controls">
          {events.length > 0 && (
            <select className="avp-event-select" value={selectedEvent || ""} onChange={e => { setQuery(""); setPaymentFilter("all"); setSelectedEvent(e.target.value); }}>
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>{ev.name}{ev.start_date ? ` · ${new Date(ev.start_date).getFullYear()}` : ""}</option>
              ))}
            </select>
          )}
          <input type="search" placeholder="Search vendors…" value={query} onChange={e => setQuery(e.target.value)} className="avp-search" />
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="avp-stats">
        <div className="avp-stat avp-stat--green">
          <span className="avp-stat-val">{counts.paid}</span>
          <span className="avp-stat-lbl">Fully paid</span>
        </div>
        <div className="avp-stat avp-stat--amber">
          <span className="avp-stat-val">{counts.deposit + counts.balance}</span>
          <span className="avp-stat-lbl">Deposit only</span>
        </div>
        <div className="avp-stat avp-stat--red">
          <span className="avp-stat-val">{counts.unpaid}</span>
          <span className="avp-stat-lbl">Unpaid</span>
        </div>
        <div className="avp-stat avp-stat--hold">
          <span className="avp-stat-val">{counts.on_hold}</span>
          <span className="avp-stat-lbl">On hold</span>
        </div>
        <div className="avp-stat">
          <span className="avp-stat-val">{counts.all}</span>
          <span className="avp-stat-lbl">Total</span>
        </div>
      </div>

      {/* ── Pills ── */}
      <div className="avp-pills">
        {PAYMENT_FILTERS.map(f => (
          <button
            key={f.value}
            className={[
              "avp-pill",
              paymentFilter === f.value ? "avp-pill--active" : "",
              f.value === "on_hold"     ? "avp-pill--hold"   : "",
            ].filter(Boolean).join(" ")}
            onClick={() => setPaymentFilter(f.value)}
          >
            {f.label} <span className="avp-pill-count">{counts[f.value]}</span>
          </button>
        ))}
      </div>

      {/* ── Export ── */}
      <div className="avp-export-bar">
        <button className="avp-export-btn" onClick={exportExtrasToExcel}>
          <Download size={14} />
          Export extras
          <span className="avp-export-note">
            ({eligibleForExport.length} vendor{eligibleForExport.length !== 1 ? "s" : ""})
          </span>
        </button>

        <button className="avp-export-btn" onClick={exportPaidVendorsToExcel}>
          <Download size={14} />
          Export paid vendors
          <span className="avp-export-note">
            ({eligibleForExport.length} vendor{eligibleForExport.length !== 1 ? "s" : ""})
          </span>
        </button>
      </div>

      {/* ── Cards ── */}
      {loading ? (
        <div className="avp-loading"><div className="avp-spinner" /><span>Loading vendors…</span></div>
      ) : (
        <div className="avp-list">
          {filtered.length === 0 && <div className="avp-empty">No vendors match this filter.</div>}

          {filtered.map(app => {
            const slot             = slots[app.id];
            const hasSlot          = !!slot;
            const isOnHold         = app.status === "on_hold";
            const depositDeadline  = getDepositDeadline(app);
            const depositCountdown = !app.deposit_submitted && depositDeadline ? getCountdown(depositDeadline) : null;
            const balanceCountdown = !app.balance_confirmed ? getCountdown(BALANCE_DEADLINE) : null;
            const depositOverdue   = depositCountdown?.urgency === "urgent" && !app.deposit_submitted;
            const isExpanded       = !!expanded[app.id];
            const slug             = app.vendors?.slug;

            const cardClass = [
              "avp-card",
              isExpanded                                                                                ? "avp-card--expanded" : "",
              isOnHold                                                                                  ? "avp-card--hold"     : "",
              !isOnHold && app.balance_confirmed                                                        ? "avp-card--paid"     : "",
              !isOnHold && app.balance_submitted && !app.balance_confirmed                               ? "avp-card--balance"  : "",
              !isOnHold && app.deposit_submitted && !app.balance_submitted && !app.balance_confirmed    ? "avp-card--deposit"  : "",
              !isOnHold && depositOverdue && !app.balance_confirmed                                     ? "avp-card--overdue"  : "",
            ].filter(Boolean).join(" ");

            return (
              <div key={app.id} className={cardClass}>

                {/* ── Top: identity + status ── */}
                <div className="avp-card-header">
                  <VendorAvatar 
                    logoUrl={app.vendors?.logo_url} 
                    brandName={app.vendors?.brand_name} 
                    slug={slug}
                  />
                  <div className="avp-card-identity">
                    <div className="avp-brand">
                      {slug ? (
                        <a 
                          href={`/vendors/${slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: "inherit", textDecoration: "none" }}
                          className="avp-brand-link"
                          title="View vendor profile"
                        >
                          {app.vendors?.brand_name || "Unnamed vendor"}
                        </a>
                      ) : (
                        app.vendors?.brand_name || "Unnamed vendor"
                      )}
                    </div>
                    <div className="avp-email">{app.vendors?.email}</div>
                  </div>
                  {isOnHold && <span className="avp-hold-badge">On Hold</span>}
                </div>

                {/* ── Primary status badge ── */}
                <div className="avp-card-status">
                  {isOnHold && <span className="avp-badge avp-badge--hold">Account suspended</span>}
                  {!isOnHold && app.balance_confirmed && <span className="avp-badge avp-badge--green">Fully paid</span>}
                  {!isOnHold && app.balance_submitted && !app.balance_confirmed && <span className="avp-badge avp-badge--purple">Balance submitted</span>}
                  {!isOnHold && app.deposit_submitted && !app.balance_submitted && !app.balance_confirmed && (
                    <span className="avp-badge avp-badge--amber">{app.deposit_confirmed ? "Deposit confirmed" : "Deposit submitted"}</span>
                  )}
                  {!isOnHold && !app.deposit_submitted && hasSlot  && <span className="avp-badge avp-badge--red">Awaiting deposit</span>}
                  {!isOnHold && !hasSlot && !app.deposit_submitted && <span className="avp-badge avp-badge--gray">No spot</span>}

                  {!isOnHold && !app.deposit_submitted && depositCountdown && (
                    <span className={`avp-deadline avp-deadline--${depositCountdown.urgency}`}>
                      dep · {depositCountdown.label}
                    </span>
                  )}
                  {!isOnHold && app.deposit_submitted && !app.balance_confirmed && balanceCountdown && (
                    <span className={`avp-deadline avp-deadline--${balanceCountdown.urgency}`}>
                      bal · {balanceCountdown.label}
                    </span>
                  )}
                </div>

                {/* ── Key facts row ── */}
                <div className="avp-card-facts">
                  <div className="avp-fact">
                    <span className="avp-fact-label"><MapPin size={11} /> Spot</span>
                    <span className="avp-fact-value">
                      {hasSlot ? slot.slot_id : (isOnHold ? "Released" : "Not assigned")}
                    </span>
                  </div>
                  <div className="avp-fact">
                    <span className="avp-fact-label">Tent type</span>
                    <span className="avp-fact-value avp-fact-value--capitalize">{app.tent_type?.replace(/_/g, " ") || "—"}</span>
                  </div>
                  <div className="avp-fact avp-fact--invoice">
                    <span className="avp-fact-label">Invoice</span>
                    <span className="avp-fact-value">{app.invoice_total?.toLocaleString() || "—"} KES</span>
                  </div>
                </div>

                {/* ── Actions ── */}
                <div className="avp-card-actions">
                  {isOnHold ? (
                    <>
                      <button
                        className="avp-btn avp-btn--activate"
                        onClick={() => activateVendor(app)}
                        disabled={sending[app.id + "_activate"]}
                      >
                        <PlayCircle size={12} />
                        {sending[app.id + "_activate"] ? "Activating…" : "Activate"}
                      </button>
                      <button className="avp-btn-icon" title="Download documents" onClick={() => downloadDocuments(app)}>
                        <Download size={13} />
                      </button>
                    </>
                  ) : (
                    <>
                      {app.deposit_submitted && !app.deposit_confirmed && !app.balance_confirmed && (
                        <button className="avp-btn" onClick={() => confirmDeposit(app)} disabled={sending[app.id + "_d"]}>
                          <ShieldCheck size={12} /> Confirm deposit
                        </button>
                      )}

                      {(app.balance_submitted || app.deposit_submitted) && !app.balance_confirmed && (
                        <button className="avp-btn" onClick={() => confirmFullPayment(app)} disabled={sending[app.id + "_f"]}>
                          <DollarSign size={12} /> Full payment
                        </button>
                      )}

                      {app.balance_confirmed && (
                        <button className="avp-btn avp-btn--muted" disabled>
                          <CheckCircle size={12} /> Paid
                        </button>
                      )}

                      <button className="avp-btn-icon" title="Download documents" onClick={() => downloadDocuments(app)}>
                        <Download size={13} />
                      </button>
                      <button
                        className="avp-btn-icon avp-btn-icon--danger"
                        title="Put on hold"
                        onClick={() => holdVendor(app)}
                        disabled={sending[app.id + "_hold"]}
                      >
                        <PauseCircle size={13} />
                      </button>
                    </>
                  )}

                  <button
                    className="avp-card-expand"
                    onClick={() => toggleExpand(app.id)}
                  >
                    Details
                    <ChevronDown size={13} className={isExpanded ? "avp-chevron--open" : ""} />
                  </button>
                </div>

                {/* ── Expanded ── */}
                {isExpanded && (
                  <div className="avp-expanded">
                    <div>
                      <div className="avp-exp-label">Extras</div>
                      {Object.entries(app.extras ?? {}).filter(([, qty]) => qty).length === 0
                        ? <span className="avp-empty-note">No extras ordered</span>
                        : Object.entries(app.extras ?? {}).map(([key, qty]) => !qty ? null : (
                          <div key={key} className="avp-exp-row">
                            <span>{key}</span>
                            <span>× {qty} — KES {(EXTRA_PRICES[key] * qty).toLocaleString()}</span>
                          </div>
                        ))}
                    </div>

                    <div>
                      <div className="avp-exp-label">Payment</div>
                      <div className="avp-exp-row"><span>Deposit submitted</span><span>{app.deposit_submitted ? "✅ Yes" : "❌ No"}</span></div>
                      <div className="avp-exp-row"><span>Deposit confirmed</span><span>{app.deposit_confirmed ? "✅ Yes" : "⏳ Pending"}</span></div>
                      <div className="avp-exp-row"><span>Balance submitted</span><span>{app.balance_submitted ? "✅ Yes" : "❌ No"}</span></div>
                      <div className="avp-exp-row"><span>Balance confirmed</span><span>{app.balance_confirmed ? "✅ Yes" : "⏳ Pending"}</span></div>
                    </div>

                    {hasSlot && (
                      <div>
                        <div className="avp-exp-label">Slot & Deadlines</div>
                        <div className="avp-exp-row"><span>ID</span><span>{slot.slot_id}</span></div>
                        <div className="avp-exp-row"><span>Mode</span><span>{slot.tent_mode}</span></div>
                        <div className="avp-exp-row"><span>Claimed</span><span>{new Date(slot.assigned_at).toLocaleString("en-KE", { timeZone: "Africa/Nairobi" })}</span></div>
                        <div className="avp-exp-row">
                          <span>Deposit deadline</span>
                          <span>
                            {depositDeadline
                              ? depositDeadline.toLocaleString("en-KE", { timeZone: "Africa/Nairobi", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                              : "—"}
                          </span>
                        </div>
                        <div className="avp-exp-row">
                          <span>Balance deadline</span>
                          <span>
                            {BALANCE_DEADLINE.toLocaleString("en-KE", { timeZone: "Africa/Nairobi", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="avp-exp-label">Contact</div>
                      <div className="avp-exp-row"><span>Phone</span><span>{app.vendors?.phone_number || "—"}</span></div>
                      <div className="avp-exp-row"><span>WhatsApp</span><span>{app.whatsapp_number || "—"}</span></div>
                      <div className="avp-exp-row"><span>Instagram</span><span>{app.vendors?.instagram || "—"}</span></div>
                    </div>

                    <div>
                      <div className="avp-exp-label">Auto-reminders</div>
                      <div className="avp-exp-row">
                        <span>Deposit reminders</span>
                        <span>{app.deposit_submitted ? "✅ No longer needed" : "🔔 Active (auto)"}</span>
                      </div>
                      <div className="avp-exp-row">
                        <span>Balance reminders</span>
                        <span>{app.balance_confirmed ? "✅ No longer needed" : app.deposit_confirmed ? "🔔 Active (auto)" : "⏳ Starts after deposit"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}